import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  BookOpen, 
  Users, 
  Calendar, 
  FileText, 
  Building2, 
  Settings, 
  HelpCircle,
  ChevronRight,
  Play
} from 'lucide-react';

export const HelpPage: React.FC = () => {
  const navigate = useNavigate();

  const helpSections = [
    {
      id: 'dashboard',
      title: 'Dashboard Utente',
      description: 'Panoramica completa delle tue candidature e attività recenti',
      icon: <Users className="h-6 w-6" />,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'from-blue-50 to-blue-100',
      path: '/help/dashboard'
    },
    {
      id: 'applications',
      title: 'Gestione Candidature',
      description: 'Come aggiungere, modificare e gestire le tue candidature',
      icon: <FileText className="h-6 w-6" />,
      color: 'from-green-500 to-green-600',
      bgColor: 'from-green-50 to-green-100',
      path: '/help/applications'
    },
    {
      id: 'calendar',
      title: 'Calendario Colloqui',
      description: 'Pianifica e gestisci i tuoi colloqui di lavoro',
      icon: <Calendar className="h-6 w-6" />,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'from-purple-50 to-purple-100',
      path: '/help/calendar'
    },
    {
      id: 'drive',
      title: 'Drive File',
      description: 'Gestisci i tuoi documenti e file personali',
      icon: <Building2 className="h-6 w-6" />,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'from-orange-50 to-orange-100',
      path: '/help/drive'
    },
    {
      id: 'settings',
      title: 'Impostazioni',
      description: 'Personalizza il tuo profilo e le preferenze',
      icon: <Settings className="h-6 w-6" />,
      color: 'from-gray-500 to-gray-600',
      bgColor: 'from-gray-50 to-gray-100',
      path: '/help/settings'
    },
    {
      id: 'faq',
      title: 'FAQ',
      description: 'Domande frequenti e risposte',
      icon: <HelpCircle className="h-6 w-6" />,
      color: 'from-indigo-500 to-indigo-600',
      bgColor: 'from-indigo-50 to-indigo-100',
      path: '/faq'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 lg:p-4 lg:pr-8 space-y-4 lg:space-y-6">
      <div className="w-full max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl">
                <BookOpen className="h-6 w-6 text-blue-700" />
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
                  Come Funziona?
                </h1>
                <p className="text-sm text-gray-600">
                  Guide complete per utilizzare al meglio la piattaforma
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sezioni Guide */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {helpSections.map((section) => (
            <div
              key={section.id}
              onClick={() => navigate(section.path)}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6 hover:shadow-lg hover:scale-[1.02] transition-all duration-200 cursor-pointer group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 bg-gradient-to-br ${section.bgColor} rounded-xl`}>
                  <div className={`text-white bg-gradient-to-r ${section.color} p-2 rounded-lg`}>
                    {section.icon}
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {section.title}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {section.description}
              </p>
              
              <div className="flex items-center gap-2 text-sm font-medium text-blue-600 group-hover:text-blue-700">
                <Play className="h-4 w-4" />
                <span>Leggi guida</span>
              </div>
            </div>
          ))}
        </div>

        {/* Sezione Informazioni Aggiuntive */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Hai bisogno di aiuto?
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Se non trovi quello che cerchi, contattaci per supporto
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/faq')}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <HelpCircle className="h-4 w-4" />
                Vai alle FAQ
              </button>
              <button
                onClick={() => navigate('/settings')}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                <Settings className="h-4 w-4" />
                Impostazioni
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 